import { formatDecimalPlace, formatNumber } from "common/formatters";
import React, { AriaAttributes, useContext, useEffect, useState } from "react";
import NP from 'number-precision';
import "./NumberEditor.scss";
import { InputGroup, FormControl, Form } from "react-bootstrap";
import { FormContext } from "common/useFormContext";

export type NumberEditorProps = {
    value?: any;
    max?: number;
    min?: number;
    step?: number;
    allowDecimals?: boolean;
    precision?: number;
    enforcePrecision?: boolean; //if precision needs to be enforced like in Price fields if 8 is entered then it has to be formatted as 8.00. Quantity field may not need that
    thousandSeparator?: boolean;
    isInvalidMessage?: string;
    onValueChange?: ((value: number) => void);
    roundingCent?: boolean;
    onAnimationEnd?: React.AnimationEventHandler<HTMLInputElement>;
    className?: string;
    ignoreInputGroupClassName?: boolean;
    required?: boolean;
    disabled?: boolean;
    placeholder?: string;
    unit?: string;
    isInputLocked?: boolean;
    validationName?: string;
    type?: string;
};


function getWholeAndDecimalPart(value: any) {

    value = value ? value.toString().replaceAll(',', '') : '';

    let wholeNumberPart: string = '';
    let decimalPart: string = '';

    if (value.toString().includes('.')) {
        const returnValueArray = value.toString().split('.');
        wholeNumberPart = returnValueArray[0];
        decimalPart = returnValueArray[1] ?? ''
    }
    else
        wholeNumberPart = value;

    return { wholeNumberPart, decimalPart };
}

type NumberEditorState = { isEditing: boolean, value: any, isInvalid: boolean}
const NumberEditor: React.FC<NumberEditorProps & AriaAttributes> = (props: NumberEditorProps & AriaAttributes) => {

    const [state, setState] = useState<NumberEditorState>({ isEditing: false, value: props.value, isInvalid: false});

    const formContext = useContext(FormContext);

    useEffect(() => {
        setState(m => ({...m, value: props.value}))
    }, [props.value])

    useEffect(() => {
        validateValue(props.value);
    }, [props.max, props.min])

    function increment() {
        if (props.value === "") {
            props.onValueChange(0);
            return;
        }
        let value: number = props.value ?? 0;
        if (props.roundingCent) {
            var remainder = NP.divide(NP.times(value, 10_000) % NP.times(props.step, 10_000), 10_000);
            const result = NP.plus(value, (NP.minus(props.step, remainder)));
            props.onValueChange(sanitizeValue(result))
        }
        else
            props.onValueChange(sanitizeValue(NP.plus(value, props.step)))
    }

    function decrement() {
        if (props.value === "") {
            props.onValueChange(0);
            return;
        }

        let value: number = props.value ?? 0;
        if (props.roundingCent) {
            var remainder = NP.divide(NP.times(value, 10_000) % NP.times(props.step, 10_000), 10_000);
            const result = NP.minus(value, (remainder === 0 ? props.step : remainder))
            props.onValueChange(sanitizeValue(result))
        }
        else
            props.onValueChange(sanitizeValue(NP.minus(value, props.step)))
    }

    function onChange(ev: React.ChangeEvent<HTMLInputElement>) {
        let value = ev.target.value ?? '';
        if (!props.allowDecimals && value.includes('.')) {
            value = value.replace('.', '');
        }
        setState(m => ({...m, isInvalid: false, value: value}))
    }

    function onKeyUp(ev: React.KeyboardEvent<HTMLInputElement>) {
        if (ev.keyCode === 13) {
            onBlur();
            setState(m => ({...m, isEditing: true}))
        }
    }

    function onBlur() {
        setState(m => ({...m, isEditing: false}));
        let value = state.value;

        const { wholeNumberPart, decimalPart } = getWholeAndDecimalPart(value);
        if (props.precision !== 0 && decimalPart !== '' && decimalPart.length > props.precision)
            value = roundingToPrecision(wholeNumberPart, decimalPart)

        value = value ? value.toString().replaceAll(",", "") : '';

        const sanitized = sanitizeValue(props.allowDecimals ? Number.parseFloat(value) : Number.parseInt(value ?? 0));
        props.onValueChange(sanitized)
    }

    function validateValue(val: number) {
        let isInvalid = false;

        if (props.allowDecimals) {
            if (val > (props.max ?? Number.POSITIVE_INFINITY))
                isInvalid = true;
        }
        else {
            if (val > (props.max ?? Number.MAX_VALUE))
                isInvalid = true;
        }
        setState(m => ({...m, isInvalid: isInvalid}))
        if (props.validationName != null)
            formContext.setField(props.validationName, !isInvalid);
    }

    function sanitizeValue(val: number): number {

        if (Number.isNaN(val)) return null;

        validateValue(val);

        if (props.allowDecimals) {
            return Math.max(
                Math.min(
                    val,
                    props.max ?? Number.POSITIVE_INFINITY),
                props.min ?? Number.NEGATIVE_INFINITY
            );
        } else {
            return Math.max(val, props.min ?? Number.MIN_VALUE);
        }
    }

    function formattedValue() {
        let returnValue = state.isEditing ? state.value : props.value;
        if (returnValue == null || returnValue === '' || returnValue === 'NaN')
            return "";
        returnValue = returnValue.toString().replaceAll(",", "");

        const { wholeNumberPart, decimalPart } = getWholeAndDecimalPart(returnValue);

        if (props.thousandSeparator) {
            if (returnValue.toString().includes('.')) {
                returnValue = formatNumber(Number.parseInt(wholeNumberPart)) + (decimalPart !== '' ? '.' + decimalPart : '.')
            }
            else {
                returnValue = formatNumber(Number.parseInt(wholeNumberPart))
            }
        }

        //on editing - below logic is to do rounding of the decimal number when it crosses the precision. 
        //for eg. if precision is set to 2 and value entered is 8.525 then on entering 5 the value is rounded to 8.53... 8.521 will become 8.52
        //This logic executes only when the decimal part's length is more than the precision, otherwise while entering 0.0, it will round the value to to 0
        if (props.precision !== 0 && decimalPart !== '' && decimalPart.length > props.precision)
            returnValue = roundingToPrecision(wholeNumberPart, decimalPart);

        // on tab out  - if enforcePrecision is set to On then on tab out we have to set the precision even when there is no decimal point set. for eg. 
        // if 8 is entered, on tab out we have to make it 8.00. While enforcing parecision we just set to 2 decimal points
        if (!state.isEditing && props.precision !== 0 && props.enforcePrecision) {
            if (!returnValue.toString().includes('.')) {
                returnValue = formatDecimalPlace(Number.parseInt(wholeNumberPart))
            }
            else if (decimalPart.length < 2)
                returnValue = formatDecimalPlace(Number.parseFloat(returnValue))
        }

        return returnValue;
    }

    function roundingToPrecision(wholeNumberPart: string, decimalPart: string): any {
        const numberFormat: string = '0,0.[' + ''.padEnd(props.precision, '0') + ']'
        const nonPrecisionedValue = wholeNumberPart + '.' + decimalPart;

        return formatNumber(Number.parseFloat(nonPrecisionedValue), numberFormat);
    }

    return (
        <InputGroup className={(props.ignoreInputGroupClassName ?? false) ? "" : "number-editor"}>
            <FormControl
                type={props.type || 'tel'}
                style={{ backgroundImage: "none" }}
                value={formattedValue()}
                max={props.max}
                min={props.min}
                step={props.step}
                onAnimationEnd={props.onAnimationEnd}

                className={props.className}
                required={props.required}
                disabled={props.disabled}
                isInvalid={state.isInvalid}
                placeholder={props.placeholder}

                onChange={!props.isInputLocked && onChange}
                onKeyUp={!props.isInputLocked && onKeyUp}
                onFocus={() => setState(m => ({...m, isEditing: true}))}
                onBlur={!props.isInputLocked && onBlur}

                aria-label={props["aria-label"]}
            />
            {(props.step != null && !props.disabled) && <InputGroup.Append className="step-buttons">
                <div className="btn-group-vertical">
                    <button type="button" onClick={(ev: React.MouseEvent) => { increment(); ev.preventDefault(); ev.stopPropagation() }}>
                        <i className="material-icons">expand_less</i>
                    </button>
                    <button type="button" onClick={(ev: React.MouseEvent) => { decrement(); ev.preventDefault(); ev.stopPropagation() }}>
                        <i className="material-icons">expand_more</i>
                    </button>
                </div>
            </InputGroup.Append>}
            {props.unit && <InputGroup.Append>
                <InputGroup.Text>{props.unit}</InputGroup.Text>
            </InputGroup.Append>}
            {state.isInvalid && <Form.Control.Feedback type="invalid">
                {props.isInvalidMessage}
            </Form.Control.Feedback>}
        </InputGroup>
    );
}

export default React.memo(NumberEditor);
